<script setup lang="ts">
import { useStatsigEventsStore } from '@apps/main/new-desktop/core/stores';
import { NightsSelector } from '@apps/main/new-desktop/modules/nightsSelector';
import { CoreEventSource } from '@apps/main/new-desktop/core/models';

import { AppSvgIcon } from '@libs/ui';

interface StandardContentDraggableProps {
  destinationName: string;
  numberOfNights?: number;
  isUserLocation?: boolean;
}

const props = defineProps<StandardContentDraggableProps>();

interface StandardContentDraggableEmits {
  (eventName: 'change-destination'): void;
  (event: 'change-number-of-nights', value: number): void;
  (eventName: 'remove-destination'): void;
}

const emit = defineEmits<StandardContentDraggableEmits>();

const statsigEventsStore = useStatsigEventsStore();
</script>

<template>
  <div class="standard-content-draggable">
    <div class="destination">
      <div class="location" @click="emit('change-destination')">
        <AppSvgIcon class="icon" name="locationMark" />
        <span class="name">{{ props.destinationName }}</span>
      </div>
      <div
        v-if="!props.isUserLocation"
        class="remove-destination"
        @click="emit('remove-destination')"
      >
        <AppSvgIcon
          name="trashCanOutlined"
          :ariaLabel="$t('a11y.remove_destination')"
        />
      </div>
    </div>
    <div v-if="!props.isUserLocation" class="nights-selector-wrapper">
      <NightsSelector
        class="nights-selector"
        :number-of-nights="props.numberOfNights"
        app-select-type="light"
        :disabled="false"
        :flicker-margin="{
          xSpacing: 'spacing-2',
        }"
        @change-number-of-nights="emit('change-number-of-nights', $event)"
        @open-number-of-nights="
          statsigEventsStore.logCoreNightsSelectorOpened({
            source: CoreEventSource.TripOverview,
          })
        "
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.standard-content-draggable {
  .destination {
    display: flex;
    align-items: center;
    flex-grow: 1;
    justify-content: space-between;
    height: 100%;

    .location {
      display: flex;
      align-items: center;
      cursor: pointer;
      width: 100%;
      height: 100%;

      transition: background-color var(--transition-short);
      padding: var(--spacing-16);

      .name {
        @include font-label-l-bold;
        padding-left: var(--spacing-8);
        @include for-less-or-equal-than-899-px {
          @include font-label-m-bold;
        }
      }

      @include on-hover {
        background-color: var(--color-neutral-30);
      }
    }

    .remove-destination {
      padding: var(--spacing-16);
      border-left: 1px solid var(--color-neutral-30);
      cursor: pointer;
      color: var(--color-neutral-70);

      @include on-hover {
        background-color: var(--color-neutral-30);
        color: var(--color-neutral-70);
      }
    }
  }
  .nights-selector-wrapper {
    display: flex;
    align-items: center;
    justify-content: start;
    flex-shrink: 0;
    height: 100%;
    width: 100%;
    padding: var(--spacing-8);
    border-top: 1px solid var(--color-neutral-30);

    .nights-selector {
      width: 110px;
    }
  }
  &:not(:first-child)::before {
    content: '';
    position: absolute;
    top: -24px;
    border: 1px solid var(--color-neutral-30);
    height: 22px;
    transform: translateX(64px);
  }
}
</style>
